/** Target column for spreadsheet import. `synonyms` extend the built-in header synonyms. */
export interface TargetColumn {
  key: string;
  synonyms?: readonly string[];
}

/** Built-in header synonyms (English and German), compared after {@link normalizeHeader}. */
export const HEADER_SYNONYMS: Readonly<Record<string, readonly string[]>> = {
  number: [
    '#',
    'ch',
    'chan',
    'channel',
    'kanal',
    'k',
    'nr',
    'no',
    'num',
    'number',
    'input',
    'in',
    'eingang',
  ],
  name: [
    'name',
    'instrument',
    'source',
    'quelle',
    'signal',
    'bezeichnung',
    'description',
    'beschreibung',
  ],
  model: [
    'mic',
    'mikro',
    'mikrofon',
    'microphone',
    'mic di',
    'model',
    'modell',
    'device',
    'gerät',
    'mic type',
    'mikrotyp',
  ],
  phantom: ['48v', '+48', '+48v', 'phantom', 'phantom power', 'phantomspeisung', 'p48'],
  patch: ['patch', 'stagebox', 'stage box', 'sb', 'box', 'line', 'patchpunkt', 'snake'],
  stand: ['stand', 'stativ', 'mic stand', 'mikrofonstativ'],
  group: ['group', 'gruppe', 'grp', 'dca', 'vca'],
  insert: ['insert', 'inserts', 'dynamics', 'comp'],
  notes: [
    'notes',
    'note',
    'comment',
    'comments',
    'remarks',
    'bemerkung',
    'bemerkungen',
    'notiz',
    'notizen',
    'anmerkung',
    'kommentar',
  ],
};

/** Lower-cases, trims and drops punctuation except `#` and `+` ("Ch." → "ch", "Mic/DI" → "mic di"). */
export function normalizeHeader(header: string): string {
  return header
    .toLowerCase()
    .replace(/[^\p{L}\p{N}#+]+/gu, ' ')
    .trim();
}

function score(header: string, target: TargetColumn): number {
  const h = normalizeHeader(header);
  if (h === '') return 0;
  const synonyms = [
    target.key,
    ...(HEADER_SYNONYMS[target.key] ?? []),
    ...(target.synonyms ?? []),
  ].map(normalizeHeader);
  if (synonyms.includes(h)) return 2;
  const words = h.split(' ');
  return synonyms.some((s) => words.includes(s)) ? 1 : 0;
}

/**
 * Guesses which target column each pasted header belongs to. Exact synonym matches beat
 * word matches; each target is used at most once (leftmost header wins ties).
 * Returns one target key (or null) per header.
 */
export function guessColumnMapping(
  headerRow: readonly string[],
  targets: readonly TargetColumn[],
): (string | null)[] {
  const candidates = headerRow
    .flatMap((header, col) =>
      targets.map((t, ti) => ({ col, ti, key: t.key, s: score(header, t) })),
    )
    .filter((c) => c.s > 0)
    .sort((a, b) => b.s - a.s || a.col - b.col || a.ti - b.ti);
  const result: (string | null)[] = headerRow.map(() => null);
  const used = new Set<string>();
  for (const c of candidates) {
    if (result[c.col] !== null || used.has(c.key)) continue;
    result[c.col] = c.key;
    used.add(c.key);
  }
  return result;
}
